import { useQuery, useQueryClient, useMutation, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { listAll as listAllDestinations } from '_/api/destinations/destinations.api';
import {
  requestFlow,
  requestFlowList,
  requestFlowSegments,
  fetchFlowSegmentByCollection,
  getFlowCommentsRequest,
  getFlowHistoryRequest,
  getFlowReadmeVersionsRequest,
  getFlowDeliveriesRequest,
  createFlowRequest,
  updateFlowRequest,
  addFlowReadmeRequest,
  updateFlowReadmeRequest,
  saveFlowCommentRequest,
  runFlowRequest,
} from '_/api/flows/flows.api';
import {
  createFlowSegmentSettings,
  deleteFlowSegmentSettings,
  getDestinationsBySegmentsIds,
  getFlowSegment,
  updateFlowSegmentSettings,
  updateOverrideDestinations,
} from '_/api/flows/flowSegments.api';
import { readmeHistoryAdapter } from '_/common/adapters/readmeHistory.adapter';
import { IReadmeVersion } from '_/common/types/readme.types';
import {
  flowDestinationsPageDataAdapter,
  segmentDestinationsAdapter,
} from '_/features/flowDestinations/flowDestinations.adapter';
import { IFlowDestinationsPageItem } from '_/features/flowDestinations/flowDestinations.types';
import { IFlow } from '_/features/flows/common/types/flow.types';
import { IFlowSegment } from '_/features/flows/common/types/flowSegments.types';
import { flowPageDataAdapter } from '_/features/flows/flowPage/flowPage.adapter';
import { IFlowPageData } from '_/features/flows/flowPage/flowPage.types';
import { flowSettingsPageItemsAdapter } from '_/features/flowSettings/flowSettings.adapter';
import { IDelivery } from '_/types/deliveries/delivery.types';
import { notification } from 'antd';

// Query keys
export const FLOW_LIST_QUERY_KEY = (projectId: string): string[] => ['flows', 'list', projectId];
export const FLOW_QUERY_KEY = (projectId: string, flowSlug: string): string[] => ['flow', projectId, flowSlug];
export const FLOW_SEGMENT_BY_COLLECTION_QUERY_KEY = (flowId: string, collectionId: string): string[] => [
  'flow',
  flowId,
  'segment',
  collectionId,
];
export const FLOW_SETTINGS_QUERY_KEY = (
  flowId: string,
  projectId?: string,
  flowSlug?: string,
  orgSlug?: string,
): (string | undefined)[] => ['flow', flowId, 'settings', projectId, flowSlug, orgSlug];
export const FLOW_COMMENTS_QUERY_KEY = (flowId: string): string[] => ['flow', flowId, 'comments'];
export const FLOW_HISTORY_QUERY_KEY = (flowId: string): string[] => ['flow', flowId, 'history'];
export const FLOW_README_VERSIONS_QUERY_KEY = (flowId: string): string[] => ['flow', flowId, 'readmeVersions'];
export const FLOW_DELIVERIES_QUERY_KEY = (flowId: string, filter?: string): string[] => [
  'flow',
  flowId,
  'deliveries',
  filter || 'all',
];
export const FLOW_DESTINATIONS_QUERY_KEY = (
  flowId: string,
  projectId?: string,
  flowSlug?: string,
  orgSlug?: string,
  projectSlug?: string,
): (string | undefined)[] => ['flow', flowId, 'destinations', projectId, flowSlug, orgSlug, projectSlug];
export const FLOW_SEGMENT_QUERY_KEY = (flowSegmentId: string): string[] => ['flowSegment', flowSegmentId];

/**
 * Hook to fetch flow list for a project
 */
export const useFlowList = (projectId?: string): UseQueryResult<IFlow[]> => {
  return useQuery({
    queryKey: FLOW_LIST_QUERY_KEY(projectId || ''),
    queryFn: async () => {
      const flows = await requestFlowList(projectId!, { include: 'project' });
      return flows;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch a single flow
 */
export const useFlow = (projectId?: string, flowSlug?: string): UseQueryResult<IFlowPageData> => {
  return useQuery({
    queryKey: FLOW_QUERY_KEY(projectId || '', flowSlug || ''),
    queryFn: async () => {
      const flow = await requestFlow(projectId!, flowSlug!, { include: 'project,readme' });
      const segments = await requestFlowSegments(flow.id);
      // Apply adapter to transform the data and extract readmeContent
      return flowPageDataAdapter({ flow: flow, segments: segments });
    },
    enabled: !!(projectId && flowSlug),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch a specific flow segment by collectionId
 */
export const useFlowSegmentByCollection = (flowId?: string, collectionId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: FLOW_SEGMENT_BY_COLLECTION_QUERY_KEY(flowId || '', collectionId || ''),
    queryFn: () => fetchFlowSegmentByCollection(flowId!, collectionId!),
    enabled: !!(flowId && collectionId),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch flow comments
 */
export const useFlowComments = (flowId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: FLOW_COMMENTS_QUERY_KEY(flowId || ''),
    queryFn: () =>
      getFlowCommentsRequest(flowId!, {
        queryParameters: {
          limit: 1000,
          sort: 'addedAt',
          sortDirection: 'DESC',
        },
        include: 'editor',
      }),
    enabled: !!flowId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to fetch flow history
 */
export const useFlowHistory = (flowId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: FLOW_HISTORY_QUERY_KEY(flowId || ''),
    queryFn: async () => {
      const response = await getFlowHistoryRequest(flowId!, {
        queryParameters: {
          limit: 20,
          sort: 'occurredAt',
          sortDirection: 'DESC',
        },
      });
      return {
        items: response?.items || [],
        cursor: response?.metadata?.cursor,
      };
    },
    enabled: !!flowId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch flow readme versions
 */
export const useFlowReadmeVersions = (flowId?: string): UseQueryResult<{ items: IReadmeVersion[] }> => {
  return useQuery({
    queryKey: FLOW_README_VERSIONS_QUERY_KEY(flowId || ''),
    queryFn: async () => {
      const versions = await getFlowReadmeVersionsRequest(flowId!);
      // Apply the adapter to transform and add metadata (editorName, published, isCurrentVersion)
      const transformedVersions = readmeHistoryAdapter(versions);
      return {
        items: transformedVersions,
      };
    },
    enabled: !!flowId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to create a flow comment
 */
export const useCreateFlowComment = (flowId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (comment: string) => saveFlowCommentRequest(flowId!, comment),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FLOW_COMMENTS_QUERY_KEY(flowId!) });
      notification.success({
        message: 'Comment Saved',
      });
    },
    onError: (error: any) => {
      console.error('Error saving comment', error);
      notification.error({
        message: error.message || 'Error saving comment',
      });
    },
  });
};

/**
 * Hook to create a flow
 */
export const useCreateFlow = (projectId?: string): UseMutationResult<any, Error, any> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (flowData: any) => createFlowRequest(projectId!, flowData),
    onSuccess: (response) => {
      // Invalidate flow list queries
      void queryClient.invalidateQueries({ queryKey: ['flows', 'list'] });
      notification.success({
        message: 'Flow Created Successfully',
      });
      return response;
    },
    onError: (error: any) => {
      console.error('Error creating flow', error);
      notification.error({
        message: error.message || 'Error creating flow',
      });
    },
  });
};

/**
 * Hook to update a flow
 */
export const useUpdateFlow = (projectId?: string, flowSlug?: string, flowId?: string): UseMutationResult<any, Error, any> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (flowData: any) => updateFlowRequest(flowId!, flowData),
    onSuccess: () => {
      // Invalidate the flow query to refetch updated data
      if (projectId && flowSlug) {
        void queryClient.invalidateQueries({ queryKey: FLOW_QUERY_KEY(projectId, flowSlug) });
      }
      // Invalidate flow history to refresh activity
      if (flowId) {
        void queryClient.invalidateQueries({ queryKey: FLOW_HISTORY_QUERY_KEY(flowId) });
      }
      notification.success({
        message: 'Flow Updated Successfully',
      });
    },
    onError: (error: any) => {
      console.error('Error updating flow', error);
      notification.error({
        message: error.message || 'Error updating flow',
      });
    },
  });
};

/**
 * Hook to run a flow
 */
export const useRunFlow = (flowId?: string): UseMutationResult<any, Error, Record<string, unknown> | undefined> => {
  return useMutation({
    mutationFn: (flowOverride: Record<string, unknown> = {}) => runFlowRequest(flowId!, flowOverride),
    onSuccess: () => {
      notification.success({
        message: 'Flow Started Successfully',
      });
    },
    onError: (error: any) => {
      console.error('Error running flow', error);
      notification.error({
        message: error.message || 'Error running flow',
      });
    },
  });
};

/**
 * Hook to add or update flow readme
 */
export const useSaveFlowReadme = (
  projectId?: string,
  flowSlug?: string,
  hasReadme?: boolean,
): UseMutationResult<any, Error, { flowId: string; content: string }> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ flowId, content }: { flowId: string; content: string }) => {
      if (hasReadme) {
        return updateFlowReadmeRequest(flowId, content);
      }
      return addFlowReadmeRequest(flowId, content);
    },
    onSuccess: (_data, variables) => {
      // Invalidate the flow query to refetch with updated readme
      if (projectId && flowSlug) {
        void queryClient.invalidateQueries({ queryKey: FLOW_QUERY_KEY(projectId, flowSlug) });
      }
      // Invalidate readme versions to show the new version
      if (variables.flowId) {
        void queryClient.invalidateQueries({
          queryKey: FLOW_README_VERSIONS_QUERY_KEY(variables.flowId),
        });
        // Invalidate flow history to refresh activity
        void queryClient.invalidateQueries({ queryKey: FLOW_HISTORY_QUERY_KEY(variables.flowId) });
      }
      notification.success({
        message: 'Readme Saved Successfully',
      });
    },
    onError: (error: any) => {
      console.error('Error saving readme', error);
      notification.error({
        message: error.message || 'Error saving readme',
      });
    },
  });
};

/**
 * Hook to fetch flow settings (segments with settings and collection included)
 */
export const useFlowSettings = (projectId?: string, flowSlug?: string, flowId?: string): UseQueryResult<any> => {
  const params = useParams({ strict: false }) as Partial<{ orgSlug: string }>;
  const orgSlug = params.orgSlug || '';

  return useQuery({
    queryKey: FLOW_SETTINGS_QUERY_KEY(flowId || '', projectId, flowSlug, orgSlug),
    queryFn: async () => {
      const flow = await requestFlow(projectId!, flowSlug!, { include: 'project' });
      const flowSegments = await requestFlowSegments(flow.id, {
        include: 'settings,collection',
        queryParameters: {
          sort: 'ordinal',
        },
      });

      // Apply adapter to add URLs and format the items
      return flowSettingsPageItemsAdapter({
        flow: flow,
        flowSegments: flowSegments,
        orgSlug: orgSlug,
      });
    },
    enabled: !!(projectId && flowSlug && flowId && orgSlug),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to create custom settings for a flow segment (unlink from collection defaults)
 */
export const useCreateFlowSegmentSettings = (
  flowSegmentId?: string,
  flowId?: string,
): UseMutationResult<any, Error, Partial<Record<string, any>> | void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data?: Record<string, any>) => createFlowSegmentSettings(flowSegmentId!, data || {}),
    onSuccess: () => {
      // Invalidate the flow segment query so this segment's settings reflect the new row
      if (flowSegmentId) {
        void queryClient.invalidateQueries({ queryKey: FLOW_SEGMENT_QUERY_KEY(flowSegmentId) });
      }
      if (flowId) {
        // FLOW_SETTINGS_QUERY_KEY takes projectId/flowSlug/orgSlug too, which aren't available
        // here -- invalidate by prefix (the first 3 elements) instead of building the full key
        // with missing args, which would produce a non-matching, wrongly-shaped key.
        void queryClient.invalidateQueries({ queryKey: ['flow', flowId, 'settings'] });
        void queryClient.invalidateQueries({ queryKey: FLOW_HISTORY_QUERY_KEY(flowId) });
      }
      notification.success({
        message: 'Flow Settings Saved',
      });
    },
    onError: (error: any) => {
      console.error('Error updating flow settings', error);
      notification.error({
        message: 'Error updating flow settings',
      });
    },
  });
};

/**
 * Hook to delete custom settings for a flow segment (link back to collection defaults)
 */
export const useDeleteFlowSegmentSettings = (flowSegmentId?: string, flowId?: string): UseMutationResult<any, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteFlowSegmentSettings(flowSegmentId!),
    onSuccess: () => {
      // Invalidate the flow segment query so this segment's settings reflect the deleted row
      if (flowSegmentId) {
        void queryClient.invalidateQueries({ queryKey: FLOW_SEGMENT_QUERY_KEY(flowSegmentId) });
      }
      if (flowId) {
        // FLOW_SETTINGS_QUERY_KEY takes projectId/flowSlug/orgSlug too, which aren't available
        // here -- invalidate by prefix (the first 3 elements) instead of building the full key
        // with missing args, which would produce a non-matching, wrongly-shaped key.
        void queryClient.invalidateQueries({ queryKey: ['flow', flowId, 'settings'] });
        void queryClient.invalidateQueries({ queryKey: FLOW_HISTORY_QUERY_KEY(flowId) });
      }
      notification.success({
        message: 'Flow Settings Saved',
      });
    },
    onError: (error: any) => {
      console.error('Error updating flow settings', error);
      notification.error({
        message: 'Error updating flow settings',
      });
    },
  });
};

/**
 * Hook to fetch flow deliveries
 */
export const useFlowDeliveries = (
  flowId?: string,
  filter?: string,
): UseQueryResult<{ items: IDelivery[]; cursor: string | null }> => {
  return useQuery({
    queryKey: FLOW_DELIVERIES_QUERY_KEY(flowId || '', filter),
    queryFn: () => getFlowDeliveriesRequest(flowId!, filter, undefined),
    enabled: !!flowId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to fetch flow destinations data
 */
export const useFlowDestinationsData = (
  projectId?: string,
  flowSlug?: string,
  flowId?: string,
  orgSlug?: string,
  projectSlug?: string,
): UseQueryResult<IFlowDestinationsPageItem[]> => {
  return useQuery({
    queryKey: FLOW_DESTINATIONS_QUERY_KEY(flowId || '', projectId, flowSlug, orgSlug, projectSlug),
    queryFn: async () => {
      // Fetch flow with segments
      const flow = await requestFlow(projectId!, flowSlug!, { include: 'project' });
      const segments = await requestFlowSegments(flow.id, {
        include: 'collection',
        queryParameters: { sort: 'ordinal' },
      });

      // Fetch org destinations
      const orgDestinations = await listAllDestinations();

      // Fetch destinations by segment IDs
      const segmentIds = segments.map((s: any) => s.id);
      const segmentDestinations = await getDestinationsBySegmentsIds(segmentIds);
      const segmentsDestinationsMap = segmentDestinationsAdapter(segmentDestinations);

      // Create collections destinations map (empty for now, collections may have their own destinations)
      const collectionsDestinationsMap = {};

      // Apply adapter
      return flowDestinationsPageDataAdapter({
        collectionsDestinationsMap: collectionsDestinationsMap,
        orgDestinations: orgDestinations,
        flow: flow,
        orgSlug: orgSlug!,
        projectSlug: projectSlug!,
        segmentsDestinationsMap: segmentsDestinationsMap,
        segments: segments,
      });
    },
    enabled: !!(projectId && flowSlug && flowId && orgSlug && projectSlug),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch a single flow segment with settings and collection
 */
export const useFlowSegment = (flowSegmentId?: string): UseQueryResult<IFlowSegment> => {
  return useQuery({
    queryKey: FLOW_SEGMENT_QUERY_KEY(flowSegmentId || ''),
    queryFn: () => getFlowSegment(flowSegmentId!, { include: 'collection,settings,collection.schema' }),
    enabled: !!flowSegmentId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to update flow segment settings
 */
export const useUpdateFlowSegmentSettings = (flowSegmentId?: string, flowId?: string): UseMutationResult<any, Error, any> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => updateFlowSegmentSettings(flowSegmentId!, data),
    onSuccess: () => {
      // Invalidate the flow segment query to refetch
      if (flowSegmentId) {
        void queryClient.invalidateQueries({ queryKey: FLOW_SEGMENT_QUERY_KEY(flowSegmentId) });
      }
      // Invalidate flow history to refresh activity
      if (flowId) {
        void queryClient.invalidateQueries({ queryKey: FLOW_HISTORY_QUERY_KEY(flowId) });
      }
      notification.success({
        message: 'Settings Updated Successfully',
      });
    },
    onError: (error: any) => {
      console.error('Error updating settings', error);
      notification.error({
        message: error.message || 'Error updating settings',
      });
    },
  });
};

/**
 * Hook to toggle flow destinations override
 */
export const useToggleFlowDestinationsOverride = (
  flowId?: string,
): UseMutationResult<any, Error, { flowSegmentId: string; overrideDestinations: boolean }> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ flowSegmentId, overrideDestinations }: { flowSegmentId: string; overrideDestinations: boolean }) =>
      updateOverrideDestinations(flowSegmentId, overrideDestinations),
    onSuccess: () => {
      // Invalidate flow destinations to refetch
      if (flowId) {
        void queryClient.invalidateQueries({ queryKey: FLOW_DESTINATIONS_QUERY_KEY(flowId) });
      }
      notification.success({
        message: 'Destinations Override Updated',
      });
    },
    onError: (error: any) => {
      console.error('Error updating destinations override', error);
      notification.error({
        message: error.message || 'Error updating destinations override',
      });
    },
  });
};
